import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faArrowUpFromBracket, faDownload, faInfoCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { useInstallApp } from '@/hooks/useInstallApp';

// Each step names the button to press as iOS labels it, next to the icon it
// wears there, so it can be picked out of the share sheet.
const IOS_STEPS: { key: string; icon?: IconDefinition }[] = [
    { key: 'share', icon: faArrowUpFromBracket },
    { key: 'addToHome', icon: faSquarePlus },
    { key: 'confirm' },
];

interface InstallAppButtonProps {
    className?: string;
}

/**
 * "Install app" on the home page. Where the browser has an install dialog of its
 * own (Chrome, Edge, Samsung Internet) the button opens it. iOS has none, and no
 * API to open one, so on an iPhone or iPad it walks through Share → Add to Home
 * Screen instead. The steps are written to hold across Safari versions: the
 * Share button sat in the toolbar up to iOS 18, went behind ••• in iOS 26 and
 * behind ≡ in iOS 27.
 *
 * Renders nothing where neither applies, or once the app is installed.
 */
const InstallAppButton: React.FC<InstallAppButtonProps> = ({ className = '' }) => {
    const { t } = useTranslation();
    const { method, install } = useInstallApp();
    const [guideOpen, setGuideOpen] = useState(false);

    // Escape closes the guide, and the page behind stays put while it is open
    useEffect(() => {
        if (!guideOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setGuideOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [guideOpen]);

    if (!method) return null;

    const closeGuide = () => setGuideOpen(false);

    return (
        <>
            <button
                type="button"
                onClick={method === 'prompt' ? install : () => setGuideOpen(true)}
                className={`glass-btn-ghost inline-flex items-center gap-2 text-sm ${className}`}
            >
                <FontAwesomeIcon icon={faDownload} aria-hidden="true"/>
                {t('home.install.button')}
            </button>

            {/* Portalled: each direct child of the page is its own stacking
                context, which would leave the overlay under the sticky header */}
            {guideOpen && createPortal(
                <div
                    className="glass-modal-overlay"
                    onClick={closeGuide}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('home.install.guide.title')}
                >
                    <div className="glass-modal w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                        {/* Centred, with the X out of the flow: beside an icon and a
                            close button the title only had room for half a line */}
                        <div className="relative text-center mb-6">
                            <button
                                type="button"
                                onClick={closeGuide}
                                className="glass-header-back !p-2 absolute -top-2 -right-2"
                                aria-label={t('home.install.guide.close')}
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-sm"/>
                            </button>
                            <img src="/images/logo192.png" alt="" width={56} height={56} className="w-14 h-14 mx-auto mb-3"/>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t('home.install.guide.title')}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {t('home.install.guide.subtitle')}
                            </p>
                        </div>

                        <ol className="space-y-4">
                            {IOS_STEPS.map((step, i) => (
                                <li key={step.key} className="flex items-start gap-3">
                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-semibold flex-shrink-0">
                                        {i + 1}
                                    </span>
                                    <div className="min-w-0 pt-0.5">
                                        <p className="text-gray-900 dark:text-white">
                                            {t(`home.install.guide.steps.${step.key}.action`)}{' '}
                                            <strong className="font-semibold">
                                                {t(`home.install.guide.steps.${step.key}.label`)}
                                            </strong>
                                            {step.icon && (
                                                <FontAwesomeIcon icon={step.icon} className="ml-2 text-indigo-600 dark:text-indigo-400" aria-hidden="true"/>
                                            )}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                            {t(`home.install.guide.steps.${step.key}.hint`)}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ol>

                        {/* In-app browsers (Zalo, Messenger...) have no Add to Home
                            Screen, and are where most links to this app get opened */}
                        <p className="glass-info-banner flex items-start gap-2 mt-6 text-sm text-gray-700 dark:text-gray-300">
                            <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5 text-indigo-600 dark:text-indigo-400" aria-hidden="true"/>
                            {t('home.install.guide.inAppNote')}
                        </p>

                        <button type="button" onClick={closeGuide} className="glass-btn-primary w-full mt-6">
                            {t('home.install.guide.gotIt')}
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default InstallAppButton;
